import { portalText } from "../../i18n/portal.js";
/**
 * Reading the file a member chose or dropped, before anything is sent (customer-portal review
 * 2026-09-25, CL #112, CL #114, CL #115).
 *
 * `File.text()` decodes as UTF-8 and replaces every byte it cannot decode with U+FFFD, silently:
 * a CSV saved in the Windows Greek code page (older Greek Excel's default "CSV") arrived with
 * every Greek name as `�����`, and saved as Ready. So the file's BYTES are read here and decoded
 * with `fatal: true`: a file that is not UTF-8 is said to be so, and the member chooses — read it
 * as Greek (Windows-1253) or Western European (Windows-1252), or save it again as CSV UTF-8. The
 * choice is theirs and visible; nothing guesses a code page for them.
 *
 * A spreadsheet file (`.xlsx`, `.xls`, `.ods`, `.numbers`) is recognised by its name or by its
 * first bytes (a ZIP's `PK\x03\x04`, an OLE file's `D0 CF 11 E0`) and refused with how to save it
 * as CSV; an empty file is refused as empty. The API refuses both too (a member using the API
 * directly gets the same answer), but saying it here costs no request.
 */

/** The code pages a member may choose for a file that is not UTF-8. */
export type LegacyEncoding = "windows-1253" | "windows-1252";

export const LEGACY_ENCODING: readonly { readonly value: LegacyEncoding; readonly label: string }[] = [
  { value: "windows-1253", get label() { return portalText("operations", "read_it_as_greek_windows_4c5df105"); } },
  { value: "windows-1252", get label() { return portalText("operations", "read_it_as_western_european_windows_57976c52"); } },
];

export type FileReading =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "refused"; readonly message: string }
  | { readonly kind: "not_utf8"; readonly byte: Uint8Array };

const SPREADSHEET_EXTENSION = /\.(xlsx|xlsm|xls|ods|numbers)$/i;

export const SPREADSHEET_MESSAGE =
  () => (portalText("operations", "this_is_a_spreadsheet_file_not_a_csv_so_no_column_can_be_r_8ce46ac3"));

export const NOT_UTF8_MESSAGE =
  () => (portalText("operations", "this_file_is_not_saved_as_utf_8_so_its_letters_would_come_a645c937"));

export const EMPTY_FILE_MESSAGE = () => (portalText("operations", "this_file_is_empty_there_is_nothing_in_it_to_read_09a7cc3c"));

function startsWith(byte: Uint8Array, prefix: readonly number[]): boolean {
  return prefix.every((value, index) => byte[index] === value);
}

/** Reads what a chosen file holds, as {@link FileReading}. Pure over the name and the bytes. */
export function readContactBytes(name: string, byte: Uint8Array): FileReading {
  if (byte.byteLength === 0) return { kind: "refused", get message() { return EMPTY_FILE_MESSAGE(); } };
  if (
    SPREADSHEET_EXTENSION.test(name) ||
    startsWith(byte, [0x50, 0x4b, 0x03, 0x04]) ||
    startsWith(byte, [0xd0, 0xcf, 0x11, 0xe0])
  ) {
    return { kind: "refused", get message() { return SPREADSHEET_MESSAGE(); } };
  }
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(byte);
    if (text.trim() === "") return { kind: "refused", get message() { return EMPTY_FILE_MESSAGE(); } };
    return { kind: "text", text };
  } catch {
    return { kind: "not_utf8", byte };
  }
}

/** The bytes decoded in the code page the member chose. */
export function decodeAs(byte: Uint8Array, encoding: LegacyEncoding): string {
  return new TextDecoder(encoding).decode(byte);
}

/** Reads a `File` (chosen or dropped). A browser that cannot read it raises. */
export async function readContactFile(file: File): Promise<FileReading> {
  const buffer = await file.arrayBuffer();
  return readContactBytes(file.name, new Uint8Array(buffer));
}
